import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { romaneioService } from '../../../../../api';
import { useOps } from '../../../../../hooks/useOps';
import {
  BlockedUser,
  BlockedUsersFilters,
  BlockedUsersPagination,
} from '../types';

export const blockedUsersQueryKeys = {
  all: ['blocked-users'] as const,
  list: (ops: string, page: number, filters: BlockedUsersFilters) =>
    [...blockedUsersQueryKeys.all, 'list', ops, page, filters] as const,
};

function mapBackendToBlockedUser(item: {
  id: number;
  numero_documento: string;
  cpf: string | null;
  cnpj: string | null;
  ops: string;
  motivo_bloqueio: string;
  block_ativo: boolean;
  created_at: string;
  created_by: string;
  updated_at: string | null;
  updated_by: string | null;
}): BlockedUser {
  return {
    id: item.id,
    numeroDocumento: item.numero_documento,
    cpf: item.cpf,
    cnpj: item.cnpj,
    ops: item.ops,
    motivoBloqueio: item.motivo_bloqueio,
    blockAtivo: item.block_ativo,
    createdAt: item.created_at,
    createdBy: item.created_by,
    updatedAt: item.updated_at,
    updatedBy: item.updated_by,
  };
}

export function useBlockedUsers() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<BlockedUsersFilters>({});
  const { currentOps, getCurrentOpsConfig } = useOps();
  const queryClient = useQueryClient();
  const limit = 20;

  const query = useQuery({
    queryKey: blockedUsersQueryKeys.list(currentOps || '', page, filters),
    queryFn: async () => {
      const opsConfig = getCurrentOpsConfig();
      if (!opsConfig?.dbName || !currentOps) {
        throw new Error('Configuração de banco de dados não encontrada');
      }

      const response = await romaneioService.getBlockedUsers({
        dbName: opsConfig.dbName,
        ops: currentOps,
        page,
        limit,
        filters: {
          block_ativo: filters.blockAtivo,
          documento: filters.documento,
          created_by: filters.createdBy,
        },
      });

      console.log('Blocked Users Response:', response);

      return {
        users: response.data?.items.map(mapBackendToBlockedUser) || [],
        pagination: response.data?.pagination || {
          page: 1,
          limit,
          total: 0,
          totalPages: 0,
        },
      };
    },
    enabled: !!currentOps && !!getCurrentOpsConfig()?.dbName,
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      documento,
      ativo,
    }: {
      documento: string;
      ativo: boolean;
    }) => {
      const opsConfig = getCurrentOpsConfig();
      if (!opsConfig?.dbName || !currentOps) {
        throw new Error('Configuração não encontrada');
      }

      return romaneioService.updateCadastroStatus({
        documento,
        ativo,
        dbName: opsConfig.dbName,
        ops: currentOps,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: blockedUsersQueryKeys.all });
    },
  });

  const goToPage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const applyFilters = useCallback((newFilters: BlockedUsersFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setPage(1);
  }, []);

  const toggleBlockStatus = useCallback(
    (documento: string, currentBlockAtivo: boolean) => {
      updateMutation.mutate({ documento, ativo: currentBlockAtivo });
    },
    [updateMutation]
  );

  return {
    users: query.data?.users || [],
    pagination: query.data?.pagination as BlockedUsersPagination | undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error?.message || null,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error?.message || null,
    page,
    filters,
    goToPage,
    applyFilters,
    clearFilters,
    toggleBlockStatus,
    refetch: query.refetch,
  };
}
